import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import DefaultOrgLogo from '../../../../dashboard/img/Singularlogo.png';
import GeneralPopup from '../../components/organisms/GeneralPopup';
import { PageHeader, PageWrapper, PopupTypes } from '../../components/partials';
import { EmptyState, Spinner, WarningMessage } from '../../components/widgets';
import AdNetworksEmptyState from '../../components/widgets/AdNetworksEmptyState';
import { getFeaturePermissions } from '../../selectors/permissions';
import { getIsAgency, getOrganizationLogo, isUserAdNetwork } from '../../selectors/user';
import AppsAPI from '../../services/apps';
import { getAdnetworkDisplayName, getPartnersOptions } from '../../utils/adNetworks';
import { useFetch } from '../../utils/customHooks';
import { PARTNER_CONFIGURATION_EVENT_PREFIX } from '../../utils/general';
import { EULA_PARTNERS, eulasMessageKey, eulasTitleKey, FAQ_LINKS } from '../consts';
import { EMPTY_STATE_TOP_PARTNERS } from '../emptyStateUtils';
import {
    GET_CONFIGURED_PARTNERS,
    GET_ORGANIZATION_OPTIONS,
    GET_PARTNER,
    GET_PARTNERS,
    SAVE_EULA_AGREEMENT,
} from '../queries';
import css from './Content.css';
import { FilterOptions } from './Filters';
import PartnersList from './PartnersList';
import PartnerConfigurationShelf, { SHELF_MODE } from './shelf/PartnerConfigurationShelf';
import { trackPartnerConfigMixpanelEvent } from './shelf/utils';
import PartnerAppSitesSummaryFactory from './summary/PartnerAppSitesSummaryFactory';
import TopArea from './TopArea';
import UserConfigurationShelf from './userConfigurationShelf/UserConfigurationShelf';

// The query has never been run before and the request is now pending
const FIRST_LOADING_NETWORK_STATUS = 1;

const translationPrefix = 'STATIC.PAGES.PARTNER_CONFIGURATION';

const appsApi = new AppsAPI();

// is first configuration of a partner (no existing configs)
const isNewPartner = (configuredPartners, partnerId) => configuredPartners.every(partner => partner.id !== partnerId);

const FILTER_OPTION_TO_TYPE = {
    [FilterOptions.APP]: 'App',
    [FilterOptions.AGENCY]: 'Agency',
    [FilterOptions.PARTNER]: 'Partner',
};

const Content = () => {
    const organizationLogo = useSelector(state => getOrganizationLogo(state));
    const { permission, typesMap, isSingleAccessLevel } = useSelector(getFeaturePermissions);
    const readOnly = permission === typesMap.read;
    const organizationOptionsResponse = useQuery(GET_ORGANIZATION_OPTIONS);
    const [saveEulaAgreement, saveEulaAgreementResponse] = useMutation(SAVE_EULA_AGREEMENT);

    const [selectedPartnerId, setSelectedPartnerId] = useState(null);
    const [editedPartnerId, setEditedPartnerId] = useState(null);
    const [editedSiteId, setEditedSiteId] = useState(null);
    const [editedAgencyId, setEditedAgencyId] = useState(null);
    const [shelfMode, setShelfMode] = useState(SHELF_MODE.CREATE);
    const [filter, setFilter] = useState(null);
    const [accountSetupRequired, setAccountSetupRequired] = useState(false);
    const [directUserConfigPartner, setDirectUserConfigPartner] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const isPartner = useSelector(isUserAdNetwork);
    const isAgency = useSelector(getIsAgency);

    const partnersResponse = useQuery(GET_PARTNERS);
    const configuredPartnersResponse = useQuery(GET_CONFIGURED_PARTNERS, { notifyOnNetworkStatusChange: true });
    const [appsResponse, getApps] = useFetch(appsApi.getAttributionApps);
    const [getPartnerDetailsForSetup] = useLazyQuery(GET_PARTNER);
    const selectedPartnerDetailsResponse = useQuery(GET_PARTNER, {
        variables: { partnerId: selectedPartnerId },
        skip: !selectedPartnerId,
    });

    const { configuredPartners = [] } = configuredPartnersResponse.data || {};
    const { partners = [] } = partnersResponse.data || {};
    const partnersOptions = getPartnersOptions(partners);
    const formattedConfiguredPartners = configuredPartners.map(partner => ({
        ...partner,
        originalDisplayName: partner.displayName,
        displayName: getAdnetworkDisplayName(partner.name, partner.displayName),
        options: partners.find(p => p.id === partner.id)?.options || {},
    }));

    const filteredConfiguredPartners =
        filter?.option === FilterOptions.PARTNER
            ? formattedConfiguredPartners.filter(({ displayName }) =>
                  displayName.toLowerCase().includes(filter.text.toLowerCase())
              )
            : formattedConfiguredPartners;

    const selectedPartner = filteredConfiguredPartners.find(({ id }) => id === selectedPartnerId);
    const selectedPartnerAccountsSupport = selectedPartnerDetailsResponse.data?.partner?.accountsSupport;
    const showMissingAgenciesWarning = !isAgency && !isPartner && selectedPartner?.missingAgenciesNames?.length > 0;
    const editedPartner = partners.find(({ id }) => id === editedPartnerId);
    const requiredEULA = editedPartner
        ? EULA_PARTNERS.includes(editedPartner?.name) &&
          !organizationOptionsResponse.data?.eulasAgreements[editedPartner?.name]
        : false;

    const onFilterChanged = ({ option, text }) => {
        const previousOption = filter?.option;
        const previousText = filter?.text;
        setFilter({ option, text });

        // Disable the selected partner so the auto selection will select the first partner after the filter.
        setSelectedPartnerId(null);

        // should not re-fetch when filter text is empty
        if (!previousText && !text) return;

        if (option === FilterOptions.APP) {
            configuredPartnersResponse.refetch({ appFilter: text, agencyFilter: null });
        } else if (option === FilterOptions.AGENCY) {
            configuredPartnersResponse.refetch({ appFilter: null, agencyFilter: text });
        } else if (previousOption === FilterOptions.APP || previousOption === FilterOptions.AGENCY) {
            // if there was a filter by app or agency but now there isn't, re-fetch without the filter
            configuredPartnersResponse.refetch({ appFilter: null, agencyFilter: null });
        }

        trackPartnerConfigMixpanelEvent('Filter', null, null, {
            type: FILTER_OPTION_TO_TYPE[option],
        });
    };

    useEffect(() => {
        const { called, loading, data } = saveEulaAgreementResponse;
        if (!called || loading) return;

        if (data?.saveEulaAgreement?.ok) {
            organizationOptionsResponse.refetch();
        }
        saveEulaAgreementResponse.reset();
    }, [saveEulaAgreementResponse]);

    // check if we got back from partner config angular page
    useEffect(() => {
        const initializeComponent = async () => {
            trackPartnerConfigMixpanelEvent('Page Loaded');

            // `source` is the legacy Angular param name for the partner; keep it as an alias
            // so old bookmarks/links like ?source=123&site=456 still open the right partner.
            const partnerId = parseInt(searchParams.get('partner') || searchParams.get('source'), 10);
            if (!partnerId) {
                return;
            }

            // should re-fetch when configured partners are cached but a new partner was saved
            if (!configuredPartnersResponse.loading && isNewPartner(configuredPartners, partnerId)) {
                await configuredPartnersResponse.refetch();
            }

            setSelectedPartnerId(partnerId);

            const siteId = parseInt(searchParams.get('site'), 10);
            if (siteId) {
                const agencyId = searchParams.get('agencyId') || '';
                if (agencyId) {
                    setEditedAgencyId(agencyId);
                }
                setEditedPartnerId(partnerId);
                setEditedSiteId(siteId);
            }

            const mode = searchParams.get('mode');
            if (mode) {
                setShelfMode(mode);
            }
        };

        initializeComponent();
    }, []);

    // background prefetch apps
    useEffect(() => getApps(false, true), []);

    useEffect(() => {
        if (!selectedPartnerId) return;

        const partnerId = parseInt(searchParams.get('partner') || searchParams.get('source'), 10);
        const siteId = parseInt(searchParams.get('site'), 10);
        const agencyId = searchParams.get('agencyId') || '';
        const mode = searchParams.get('mode') || '';

        if (
            partnerId !== selectedPartnerId ||
            siteId !== editedSiteId ||
            agencyId !== editedAgencyId ||
            mode !== shelfMode
        ) {
            setSearchParams({
                partner: selectedPartnerId,
                mode: shelfMode,
                ...(editedSiteId ? { site: editedSiteId } : {}),
                ...(editedAgencyId ? { agencyId: editedAgencyId } : {}),
            });
        }
    }, [selectedPartnerId, editedSiteId, editedAgencyId, searchParams, shelfMode]);

    const saveEula = async () => {
        await saveEulaAgreement({ variables: { partnerName: editedPartner.name } });
    };

    const closePartnerConfigShelf = () => {
        setEditedSiteId(null);
        setEditedPartnerId(null);
        setAccountSetupRequired(false);
    };

    const openPartnerConfigShelf = (partnerId, siteId, agencyId, mode) => {
        setEditedAgencyId(agencyId);
        setEditedPartnerId(partnerId);
        setEditedSiteId(siteId);
        setShelfMode(mode);
    };

    const checkAccountSetupRequired = async partnerId => {
        try {
            // Fetch partner details for the specific partnerId (using no-cache to avoid affecting selectedPartnerDetailsResponse)
            const { data: partnerData } = await getPartnerDetailsForSetup({ variables: { partnerId } });
            // Always require account setup for partners with accounts support (user configuration flow)
            return partnerData?.partner?.accountsSupport ?? false;
        } catch {
            // Fail-open: proceed to shelf if checks fail
            return false;
        }
    };

    const onAppSiteForPartner = async (partnerId, siteId = null, agencyId = null, mode = SHELF_MODE.CREATE) => {
        const isCreatingFirstConfig = mode === SHELF_MODE.CREATE && isNewPartner(configuredPartners, partnerId);
        const needsAccountSetup = isCreatingFirstConfig && (await checkAccountSetupRequired(partnerId));

        setAccountSetupRequired(needsAccountSetup);
        openPartnerConfigShelf(partnerId, siteId, agencyId, mode);
    };

    const onUserConfigurationForPartner = partner => {
        const partnerNode = partners.find(p => p.id === partner.id);
        setDirectUserConfigPartner(partnerNode);
    };

    // we don't want to show loading when re-fetching because of filters
    if (partnersResponse.loading || configuredPartnersResponse.networkStatus === FIRST_LOADING_NETWORK_STATUS) {
        return <Spinner show expanded />;
    }

    // we don't want to show empty state when there is no partners because of filters
    if (!configuredPartners.length && !filter && !isPartner && !editedPartner && !readOnly && isSingleAccessLevel) {
        return (
            <PageWrapper>
                <PageHeader text="STATIC.PAGE_HEADERS.PARTNER_CONFIGURATION" subText={FAQ_LINKS} />
                <AdNetworksEmptyState
                    adNetworkOptions={partnersOptions}
                    emptyStateAdNetworks={EMPTY_STATE_TOP_PARTNERS}
                    translationPageKey="PARTNER_CONFIGURATION"
                    mixpanelPrefix={PARTNER_CONFIGURATION_EVENT_PREFIX}
                    onAdNetworkSelectionChange={({ id }) => onAppSiteForPartner(id)}
                    orgLogoPath={organizationLogo || DefaultOrgLogo}
                />
            </PageWrapper>
        );
    }

    return (
        <PageWrapper className={css.container} allowDropDownPageScrolling disablePadding>
            <TopArea
                filter={filter}
                filterLoading={configuredPartnersResponse.loading}
                onFilterChanged={onFilterChanged}
                partnersOptions={partnersOptions}
                onAddSiteForPartner={onAppSiteForPartner}
                selectedPartner={selectedPartnerId}
            />
            <div className={css.mainArea}>
                <PartnersList
                    autoSelectItem
                    selectedPartner={selectedPartnerId}
                    partners={filteredConfiguredPartners}
                    className={css.leftArea}
                    onPartnerSelected={setSelectedPartnerId}
                    filter={filter?.option === FilterOptions.PARTNER ? filter.text : ''}
                />
                <div className={classNames(css.rightArea, { [css.withWarning]: showMissingAgenciesWarning })}>
                    {selectedPartner ? (
                        <>
                            {showMissingAgenciesWarning && (
                                <WarningMessage
                                    message={`${translationPrefix}.MISSING_AGENCIES_MESSAGE`}
                                    show
                                    showIcon={false}
                                    showSpacer={false}
                                    type="warning"
                                    className={css.missingAgenciesWarning}
                                    textReplace={{
                                        agenciesNames: selectedPartner.missingAgenciesNames?.join(', '),
                                    }}
                                />
                            )}
                            <PartnerAppSitesSummaryFactory
                                partner={selectedPartner}
                                accountsSupport={selectedPartnerAccountsSupport}
                                appFilter={filter?.option === FilterOptions.APP ? filter.text : ''}
                                agencyFilter={filter?.option === FilterOptions.AGENCY ? filter.text : ''}
                                onAppSiteForPartner={onAppSiteForPartner}
                                onUserConfigurationForPartner={onUserConfigurationForPartner}
                            />
                        </>
                    ) : readOnly || !isSingleAccessLevel ? (
                        <div className={css.emptyStateContainer}>
                            <PageHeader text="STATIC.PAGE_HEADERS.PARTNER_CONFIGURATION" subText={FAQ_LINKS} />
                            <EmptyState
                                header={`${translationPrefix}.VIEW_MODE_EMPTY_STATE_HEADER`}
                                subHeader={`${translationPrefix}.VIEW_MODE_EMPTY_STATE_SUBHEADER`}
                            />
                        </div>
                    ) : null}
                    {editedPartner && requiredEULA && !isPartner ? (
                        <GeneralPopup
                            open={requiredEULA}
                            type={PopupTypes.INFO_WITH_CANCEL}
                            title={eulasTitleKey(editedPartner.name)}
                            text={eulasMessageKey(editedPartner.name)}
                            rejectText="STATIC.BUTTONS.CANCEL"
                            acceptText="STATIC.BUTTONS.DONE"
                            onAccept={saveEula}
                            onReject={closePartnerConfigShelf}
                            loading={saveEulaAgreementResponse.loading}
                        />
                    ) : (editedPartner && accountSetupRequired) || directUserConfigPartner ? (
                        <UserConfigurationShelf
                            open
                            partner={directUserConfigPartner || editedPartner}
                            onClose={
                                directUserConfigPartner
                                    ? () => setDirectUserConfigPartner(null)
                                    : closePartnerConfigShelf
                            }
                            onDone={
                                accountSetupRequired && !directUserConfigPartner
                                    ? () => setAccountSetupRequired(false)
                                    : null
                            }
                        />
                    ) : editedPartner ? (
                        <PartnerConfigurationShelf
                            open
                            mode={shelfMode}
                            partner={editedPartner}
                            siteId={editedSiteId}
                            agencyId={editedAgencyId}
                            apps={appsResponse.data?.apps}
                            loading={appsResponse.isLoading}
                            onClose={closePartnerConfigShelf}
                        />
                    ) : null}
                </div>
            </div>
        </PageWrapper>
    );
};

export default Content;
